'use client';

import React from 'react';

import SliderControl, {
  Props as SliderControlProps,
} from '@/components/Controls/SliderControl';

import { useDemoContext } from './DemoContext';

type FilteredSliderControlProps = Omit<
  SliderControlProps,
  'value' | 'setValue'
>;

interface Props extends FilteredSliderControlProps {
  highlightControlWhenActive?: boolean;
}

function DemoSliderControl({
  id,
  wrapperStyle = {},
  highlightControlWhenActive,
  ...props
}: Props) {
  const context = useDemoContext();

  const {
    id: demoUnitId,
    state,
    setState,
    activeControlId,
    setActiveControlId,
    initialValuesRef,
  } = context;
  const value = state[id];
  const htmlId = `${demoUnitId}_${id}`;

  const isActive = activeControlId === id;

  const handleDeactivation = React.useCallback(() => {
    setActiveControlId(null);
  }, [setActiveControlId]);

  // When this control becomes active, register a window-level `pointerup` event handler to unset it. This is to prevent edge-case issues with the active status not getting lifted when the user stops engaging with a control (since the pointerUp event might not happen ON the element in question).
  React.useEffect(() => {
    if (!isActive) {
      return;
    }

    window.addEventListener('pointerup', handleDeactivation);

    return () => {
      window.removeEventListener('pointerup', handleDeactivation);
    };
  }, [isActive, setActiveControlId, id, handleDeactivation]);

  const handleReset = React.useCallback(() => {
    setState((state) => ({
      ...state,
      [id]: initialValuesRef.current[id],
    }));
  }, [id, setState, initialValuesRef]);

  const handleActivation = React.useCallback(() => {
    setActiveControlId(id);
  }, [id, setActiveControlId]);

  const augmentedWrapperStyle = React.useMemo(() => {
    const isHighlighted =
      activeControlId === id && highlightControlWhenActive;

    if (!isHighlighted) {
      return wrapperStyle;
    }

    return {
      ...wrapperStyle,
      '--preview-color': 'var(--wham-primary-500)',
      color: 'var(--wham-primary-500)',
    };
  }, [activeControlId, id, highlightControlWhenActive, wrapperStyle]);

  return (
    <SliderControl
      id={htmlId}
      handleReset={handleReset}
      value={value}
      setValue={(newValue: any) => {
        setState((state) => ({
          ...state,
          [id]: newValue,
        }));
      }}
      wrapperStyle={augmentedWrapperStyle}
      onPointerDown={handleActivation}
      onFocus={handleActivation}
      onBlur={handleDeactivation}
      {...props}
    />
  );
}

export default DemoSliderControl;
